import React, { useState, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Toast } from 'primereact/toast';
import { AutoComplete } from 'primereact/autocomplete';
import { Editor } from 'primereact/editor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import employeeService from '../../../services/data/employee.service';
import performanceService from '../../../services/data/performance.service';
import { renderHeaderEditor } from '../../../helpers/editorToolbar';


export default function FormRequestFeedback({ onSuccess, preselected }) {
    const toast = useRef(null);
    const [loading, setLoading] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [rawResults, setRawResults] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [selectedEmployees, setSelectedEmployees] = useState(
        preselected?.id && preselected?.name ? [preselected] : []
    );
    const headerEditor = renderHeaderEditor();

    function searchEmployee(event) {
        if (!event.query || event.query.length < 2) {
            setSuggestions([]);
            return;
        }
        employeeService.autoCompleteEmployee(event.query).then((response) => {
            setRawResults(response);
            setSuggestions(response.map((item) => item.user));
        }).catch(() => setSuggestions([]));
    }

    function addEmployee(name) {
        const match = rawResults.find((item) => item.user === name);
        if (!match) return;
        if (selectedEmployees.some((e) => e.id === match.id)) {
            toast.current.show({ severity: 'info', summary: 'Já adicionado', detail: `${name} já está na lista.`, life: 2000 });
            return;
        }
        setSelectedEmployees((prev) => [...prev, { id: match.id, name }]);
        setSearchValue('');
        setSuggestions([]);
    }

    function removeEmployee(id) {
        setSelectedEmployees((prev) => prev.filter((e) => e.id !== id));
    }

    const formik = useFormik({
        initialValues: { title: '', description: '' },
        validationSchema: Yup.object({
            title: Yup.string().required('Informe o título').max(350, 'Máximo 350 caracteres'),
            description: Yup.string().required('Descreva o que deseja como feedback').min(10, 'Mínimo de 10 caracteres'),
        }),
        onSubmit: (values, { resetForm }) => {
            if (selectedEmployees.length === 0) {
                toast.current.show({ severity: 'warn', summary: 'Atenção', detail: 'Adicione ao menos um colaborador.', life: 3000 });
                return;
            }
            setLoading(true);
            const company_id = localStorage.getItem('companyId');
            const requests = selectedEmployees.map((emp) =>
                performanceService.createFeedbackRequest({
                    company_id,
                    employee_id: emp.id,
                    title: values.title,
                    description: values.description,
                })
            );
            Promise.all(requests).then(() => {
                toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Solicitação enviada!', life: 3000 });
                resetForm();
                setSearchValue('');
                setRawResults([]);
                setSelectedEmployees([]);
                if (onSuccess) setTimeout(() => onSuccess(), 1500);
            }).catch((error) => {
                const msg = error?.response?.data?.[0] || 'Erro ao enviar solicitação';
                toast.current.show({ severity: 'warn', summary: 'Ops', detail: msg, life: 4000 });
            }).finally(() => setLoading(false));
        },
    });

    return (
        <>
            <Toast ref={toast} position="top-right" />

            <form onSubmit={formik.handleSubmit}>
                <div className="mb-3">
                    <label className="form-label fw-bold">
                        Colaboradores <span className="text-danger">*</span>
                    </label>
                    <AutoComplete
                        value={searchValue}
                        suggestions={suggestions}
                        completeMethod={searchEmployee}
                        onChange={(e) => setSearchValue(e.value)}
                        onSelect={(e) => addEmployee(e.value)}
                        placeholder="Digite o nome para buscar e adicionar..."
                        inputClassName="w-100 rounded"
                        className="w-100 rounded"
                    />

                    {selectedEmployees.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mt-2">
                            {selectedEmployees.map((emp) => (
                                <span
                                    key={emp.id}
                                    className="badge d-inline-flex align-items-center gap-1 px-2 py-2"
                                    style={{ backgroundColor: '#e8f4fd', color: '#51a9b1', fontSize: '0.82rem', fontWeight: 500, borderRadius: 20 }}
                                >
                                    <FontAwesomeIcon icon={solid('user')} style={{ fontSize: '0.7rem' }} />
                                    {emp.name}
                                    <button
                                        type="button"
                                        className="btn-close btn-close-sm ms-1"
                                        style={{ fontSize: '0.5rem' }}
                                        onClick={() => removeEmployee(emp.id)}
                                        aria-label="Remover"
                                    />
                                </span>
                            ))}
                        </div>
                    )}

                    {selectedEmployees.length === 0 && formik.submitCount > 0 && (
                        <span className="text-danger small">Adicione ao menos um colaborador</span>
                    )}
                </div>

                <div className="mb-3">
                    <label className="form-label fw-bold">
                        Título <span className="text-danger">*</span>
                    </label>
                    <input
                        name="title"
                        type="text"
                        className="form-control"
                        placeholder="Ex: Feedback sobre o projeto X"
                        value={formik.values.title}
                        onChange={formik.handleChange}
                    />
                    {formik.errors.title && (
                        <span className="text-danger small">{formik.errors.title}</span>
                    )}
                </div>

                <div className="mb-3">
                    <label className="form-label fw-bold">
                        O que você gostaria de saber? <span className="text-danger">*</span>
                    </label>
                    <Editor
                        className="noToolbar"
                        style={{ height: '120px' }}
                        headerTemplate={headerEditor}
                        placeholder="Descreva o contexto e o que deseja saber..."
                        value={formik.values.description}
                        onTextChange={(e) => formik.setFieldValue('description', e.htmlValue)}
                    />
                    {formik.errors.description && (
                        <span className="text-danger small">{formik.errors.description}</span>
                    )}
                </div>

                <div className="d-flex gap-2 justify-content-start">
                    <button type="submit" className="btn btn-success btn-sm btn-rounded px-3" disabled={loading}>
                        {loading ? 'Enviando...' : (
                            selectedEmployees.length > 1
                                ? `Solicitar para ${selectedEmployees.length} pessoas`
                                : 'Solicitar Feedback'
                        )}
                    </button>
                </div>
            </form>
        </>
    );
}
